import React, { ComponentPropsWithRef, FC, useEffect, useState } from 'react'

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
  MouseSensor,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import styled from 'styled-components'

import {
  CommonContentsNavigationItem,
  CreateCommonContentPage,
  CommonContentsNavigationSortItem,
} from 'src/components/organisms'

import {
  useCommonContentPage,
  useCommonContentPageLoading,
  useGetCommonContentPages,
  useToast,
  useAuthority,
} from 'src/hooks'
import { SelectableCommonContentPageEntity } from 'src/types'

const List = styled.ul`
  box-shadow: 0 5px 20px rgba(122, 137, 160, 0.1);
  border-radius: var(--s-2);
  overflow: hidden;
`

type DivProps = ComponentPropsWithRef<'div'>
type Props = DivProps & {
  commonContents: SelectableCommonContentPageEntity[]
}

export const CommonContentsNavigation: FC<Props> = ({ commonContents, ...otherProps }) => {
  const [dndCommonContents, setDndCommonContents] = useState(commonContents)

  const { reorderCommonContentPages } = useCommonContentPage()
  const { refetch } = useGetCommonContentPages()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { showCommonContentPageLoading, hideCommonContentPageLoading } =
    useCommonContentPageLoading()
  const { canWriteCommonContent } = useAuthority()

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        // 5pxドラッグした時にソート機能を有効にする。この対応をする事でドラッグ可能領域内のクリック操作を可能にする。
        distance: 5,
      },
    })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over) {
      return
    }

    if (active.id !== over.id) {
      showCommonContentPageLoading()

      const oldIndex = dndCommonContents.map(item => item.id).indexOf(active.id as number)
      const newIndex = dndCommonContents.map(item => item.id).indexOf(over.id as number)

      const updatedCommonContents = arrayMove(dndCommonContents, oldIndex, newIndex)
      setDndCommonContents(updatedCommonContents)

      try {
        await reorderCommonContentPages({
          updatedCommonContentPageIds: updatedCommonContents.map(commonContent => commonContent.id),
        })
        refetch()
        displayStandardTextToast('保存しました')
      } catch {
        displayErrorTextToast('並び替えに失敗しました')
      } finally {
        hideCommonContentPageLoading()
      }
    }
  }

  useEffect(() => {
    const sortedCommonContents = [...commonContents].sort((a, b) => a.priority - b.priority)
    setDndCommonContents(sortedCommonContents)
  }, [commonContents])

  return (
    <div {...otherProps}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={dndCommonContents}
          strategy={verticalListSortingStrategy}
          disabled={!canWriteCommonContent}
        >
          <List className="tw-border-border-secondary">
            {dndCommonContents.map(commonContent => (
              <CommonContentsNavigationSortItem key={commonContent.id} id={commonContent.id}>
                <li className="tw-border-b tw-border-border-secondary">
                  <CommonContentsNavigationItem commonContent={commonContent} />
                </li>
              </CommonContentsNavigationSortItem>
            ))}
            {canWriteCommonContent && (
              <li>
                <CreateCommonContentPage commonContentPagesCount={dndCommonContents.length} />
              </li>
            )}
          </List>
        </SortableContext>
      </DndContext>
    </div>
  )
}
